export function ComparingFinancingOptions() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Comparing Seller Financing to Other Options
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Understanding how seller financing stacks up against traditional sales, 1031 exchanges, and other alternatives
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Comprehensive Comparison Chart
          </h3>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-4 font-bold text-gray-900">Feature</th>
                  <th className="p-4 font-bold text-teal-600">Seller Financing</th>
                  <th className="p-4 font-bold text-gray-700">Traditional Sale</th>
                  <th className="p-4 font-bold text-gray-700">1031 Exchange</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="p-4 font-semibold text-gray-900">Closing Speed</td>
                  <td className="p-4 text-teal-600 font-bold">7-30 days</td>
                  <td className="p-4 text-gray-700">60-120 days</td>
                  <td className="p-4 text-gray-700">45-180 days (strict deadlines)</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="p-4 font-semibold text-gray-900">Passive Income</td>
                  <td className="p-4 text-teal-600 font-bold">Yes (6-10%+ interest)</td>
                  <td className="p-4 text-gray-700">No</td>
                  <td className="p-4 text-gray-700">Yes (from new property)</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-4 font-semibold text-gray-900">Tax Deferral</td>
                  <td className="p-4 text-teal-600 font-bold">Yes (installment sale)</td>
                  <td className="p-4 text-gray-700">No (pay all taxes year 1)</td>
                  <td className="p-4 text-gray-700">Yes (complete deferral)</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="p-4 font-semibold text-gray-900">Sale Price Premium</td>
                  <td className="p-4 text-teal-600 font-bold">10-20% higher</td>
                  <td className="p-4 text-gray-700">Market value</td>
                  <td className="p-4 text-gray-700">Market value</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-4 font-semibold text-gray-900">Buyer Pool</td>
                  <td className="p-4 text-teal-600 font-bold">3-5X larger</td>
                  <td className="p-4 text-gray-700">Limited to bank-qualified</td>
                  <td className="p-4 text-gray-700">Limited to bank-qualified</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="p-4 font-semibold text-gray-900">As-Is Sale Possible</td>
                  <td className="p-4 text-teal-600 font-bold">Yes</td>
                  <td className="p-4 text-gray-700">Difficult</td>
                  <td className="p-4 text-gray-700">Difficult</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-4 font-semibold text-gray-900">Ongoing Management</td>
                  <td className="p-4 text-teal-600 font-bold">Minimal (collect payments)</td>
                  <td className="p-4 text-gray-700">None</td>
                  <td className="p-4 text-gray-700">High (new property)</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="p-4 font-semibold text-gray-900">Complexity</td>
                  <td className="p-4 text-teal-600 font-bold">Moderate</td>
                  <td className="p-4 text-gray-700">Low</td>
                  <td className="p-4 text-gray-700">High (strict rules)</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-4 font-semibold text-gray-900">Exit from Real Estate</td>
                  <td className="p-4 text-teal-600 font-bold">Yes (gradual)</td>
                  <td className="p-4 text-gray-700">Yes (immediate)</td>
                  <td className="p-4 text-gray-700">No (must buy replacement)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-4 font-semibold text-gray-900">Total Return Potential</td>
                  <td className="p-4 text-teal-600 font-bold">Highest (price + interest)</td>
                  <td className="p-4 text-gray-700">Moderate</td>
                  <td className="p-4 text-gray-700">High (if new property appreciates)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/0228913cdc6987402860a0b88e928fbf.jpg"
              alt="Comparison of seller financing versus other real estate options"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>

          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              When Seller Financing Makes the Most Sense
            </h3>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <div>
                    <strong className="text-gray-900 text-lg">You Want Passive Income:</strong>
                    <p className="text-gray-700">Prefer steady monthly payments over a lump sum</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <div>
                    <strong className="text-gray-900 text-lg">Tax Management:</strong>
                    <p className="text-gray-700">Want to spread capital gains over multiple years</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <div>
                    <strong className="text-gray-900 text-lg">Property Needs Work:</strong>
                    <p className="text-gray-700">Avoid costly repairs by selling as-is to investors</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <div>
                    <strong className="text-gray-900 text-lg">Maximize Sale Price:</strong>
                    <p className="text-gray-700">Command premium pricing for providing financing</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <div>
                    <strong className="text-gray-900 text-lg">Fast Sale Needed:</strong>
                    <p className="text-gray-700">Close quickly without bank delays and bureaucracy</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <div>
                    <strong className="text-gray-900 text-lg">Exit Real Estate:</strong>
                    <p className="text-gray-700">Don't want to manage another property (unlike 1031)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Seller Financing vs. 1031 Exchange: Key Differences
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-teal-50 rounded-lg p-6">
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Seller Financing Advantages</h4>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Exit real estate completely while deferring taxes</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>No property management responsibilities</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Earn interest income on top of sale price</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>No strict 45/180 day deadlines</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Simpler process with fewer restrictions</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Can sell as-is without repairs</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Higher sale price potential</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-2xl font-bold text-gray-900 mb-4">1031 Exchange Advantages</h4>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-gray-600 mr-2 flex-shrink-0"></i>
                  <span>Complete tax deferral (no taxes until final sale)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-gray-600 mr-2 flex-shrink-0"></i>
                  <span>Continue building real estate portfolio</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-gray-600 mr-2 flex-shrink-0"></i>
                  <span>Potential for continued appreciation</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-gray-600 mr-2 flex-shrink-0"></i>
                  <span>Can upgrade to better properties</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-gray-600 mr-2 flex-shrink-0"></i>
                  <span>Step-up in basis at death (estate planning)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-gray-600 mr-2 flex-shrink-0"></i>
                  <span>Maintain active real estate investor status</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-gray-600 mr-2 flex-shrink-0"></i>
                  <span>Diversify into different markets or property types</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-md">
            <div className="flex items-start">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                <i className="ri-lightbulb-line text-2xl text-blue-600"></i>
              </div>
              <div className="ml-4">
                <h4 className="text-lg font-bold text-gray-900 mb-2">Pro Tip: You Can Combine Both!</h4>
                <p className="text-gray-700 leading-relaxed">
                  Some sellers use a partial 1031 exchange combined with seller financing. Exchange part of the proceeds into a new property while seller-financing the remainder. This provides diversification, tax deferral, and passive income. Consult with a qualified intermediary and tax advisor to explore this strategy.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-8 md:p-12 text-white">
          <h3 className="text-3xl font-bold mb-6 text-center">The Bottom Line</h3>
          <p className="text-xl text-gray-300 text-center max-w-4xl mx-auto leading-relaxed mb-8">
            Seller financing offers unique advantages that make it an attractive option for many apartment building owners. While traditional sales and 1031 exchanges have their place, seller financing provides the best combination of tax benefits, passive income, higher sale prices, and flexibility for sellers who want to exit real estate while maximizing returns.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">Higher Returns</div>
              <div className="text-gray-300">Price premium + interest income</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">Tax Benefits</div>
              <div className="text-gray-300">Spread gains over multiple years</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">More Flexibility</div>
              <div className="text-gray-300">Fewer restrictions than 1031</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}